import React, { Suspense } from "react";

import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import ErrorBoundary from "./components/common/ErrorBoundary";
import LoadingSpinner from "./components/common/LoadingSpinner";
import ProtectedRoute from "./components/common/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

// Layouts
import GuestLayout from "./layouts/GuestLayout";
import UserLayout from "./layouts/UserLayout";
import AdminLayout from "./layouts/AdminLayout";

// Lazy load pages
const HomePage = React.lazy(() => import("./pages/HomePage"));
const ProductListPage = React.lazy(() => import("./pages/ProductListPage"));
const ProductDetailPage = React.lazy(() => import("./pages/ProductDetailPage"));
const CartPage = React.lazy(() => import("./pages/CartPage"));
const CheckoutPage = React.lazy(() => import("./pages/CheckoutPage"));
const PaymentNoticePage = React.lazy(() => import("./pages/PaymentNoticePage"));
const PayPalReturnPage = React.lazy(() => import("./pages/PayPalReturnPage"));
const PayPalCancelPage = React.lazy(() => import("./pages/PayPalCancelPage"));
const OrderConfirmationPage = React.lazy(
  () => import("./pages/OrderConfirmationPage"),
);
const UserProfilePage = React.lazy(() => import("./pages/UserProfilePage"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const AdminProductsPage = React.lazy(() => import("./pages/AdminProductsPage"));
const AdminOrdersPage = React.lazy(() => import("./pages/AdminOrdersPage"));
const AdminPaymentNoticesPage = React.lazy(
  () => import("./pages/AdminPaymentNoticesPage"),
);
const AdminUsersPage = React.lazy(() => import("./pages/AdminUsersPage"));
const AdminAdminsPage = React.lazy(() => import("./pages/AdminAdminsPage"));
const AdminCategoriesPage = React.lazy(
  () => import("./pages/AdminCategoriesPage"),
);
const AdminCouponsPage = React.lazy(() => import("./pages/AdminCouponsPage"));
const AboutUsPage = React.lazy(() => import("./pages/AboutUsPage"));
const AdminAboutUsPage = React.lazy(() => import("./pages/AdminAboutUsPage"));
const RkichaHomePage = React.lazy(() => import("./pages/RkichaHomePage"));
const AdminRkichaHomePage = React.lazy(
  () => import("./pages/AdminRkichaHomePage"),
);
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const AdminContactMessagesPage = React.lazy(
  () => import("./pages/AdminContactMessagesPage"),
);
const AdminBannersPage = React.lazy(() => import("./pages/AdminBannersPage"));
const AdminSettingsPage = React.lazy(() => import("./pages/AdminSettingsPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));

// Fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center py-20">
    <LoadingSpinner size="large" message="Loading..." />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <CartProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* ===== Guest Routes (public) ===== */}
                <Route element={<GuestLayout />}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductListPage />} />
                  <Route
                    path="/products/:slug"
                    element={<ProductDetailPage />}
                  />
                  <Route path="/about" element={<AboutUsPage />} />
                  <Route path="/rkicha-home" element={<RkichaHomePage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/cart" element={<CartPage />} />
                </Route>

                {/* ===== User Routes (authenticated customer) ===== */}
                <Route
                  element={
                    <ProtectedRoute allowedRoles={["customer", "admin"]} />
                  }
                >
                  <Route element={<UserLayout />}>
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/payment-notice" element={<PaymentNoticePage />} />
                    <Route
                      path="/checkout/paypal/return"
                      element={<PayPalReturnPage />}
                    />
                    <Route
                      path="/checkout/paypal/cancel"
                      element={<PayPalCancelPage />}
                    />
                    <Route
                      path="/order-confirmation"
                      element={<OrderConfirmationPage />}
                    />
                    <Route path="/profile" element={<UserProfilePage />} />
                  </Route>
                </Route>

                {/* ===== Admin Routes (admin only) ===== */}
                <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
                  <Route element={<AdminLayout />}>
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route
                      path="/admin/products"
                      element={<AdminProductsPage />}
                    />
                    <Route path="/admin/orders" element={<AdminOrdersPage />} />
                    <Route
                      path="/admin/payment-notices"
                      element={<AdminPaymentNoticesPage />}
                    />
                    <Route path="/admin/admins" element={<AdminAdminsPage />} />
                    <Route path="/admin/users" element={<AdminUsersPage />} />
                    <Route path="/admin/categories" element={<AdminCategoriesPage />} />
                    <Route path="/admin/coupons" element={<AdminCouponsPage />} />
                     <Route path="/admin/about" element={<AdminAboutUsPage />} />
                    <Route
                      path="/admin/rkicha-home"
                      element={<AdminRkichaHomePage />}
                    />
                    <Route
                      path="/admin/messages"
                      element={<AdminContactMessagesPage />}
                    />
                    <Route
                      path="/admin/banners"
                      element={<AdminBannersPage />}
                    />
                    <Route
                      path="/admin/settings"
                      element={<AdminSettingsPage />}
                    />
                  </Route>
                </Route>

                {/* 404 */}
                <Route
                  path="*"
                  element={
                    <div className="min-h-screen flex items-center justify-center bg-info">
                      <div className="text-center">
                        <h1 className="text-6xl font-bold text-secondary mb-4">
                          404
                        </h1>
                        <p className="text-gray-500 mb-8">
                          The page you are looking for does not exist.
                        </p>
                        <a
                          href="/"
                          className="bg-secondary text-white px-6 py-3 rounded-lg font-medium hover:bg-info-dark transition-colors"
                        >
                          Return to Homepage
                        </a>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </Suspense>
          </CartProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
